import React from 'react';
import styled from 'styled-components';
import { Button, ButtonIcon, Text } from '@companycam/slab-web';
import { parseISO } from 'date-fns';

import toast from 'helpers/toast';
import steven from 'helpers/steven';

import TranscriptItem from './TranscriptItem';
import { formatDate } from 'helpers/intl-helper';
import { translate } from 'javascripts/translations/i18n';
import { CaptureOutputRequestObject } from 'data/v3/data-contracts';

// #region styled-components
const TranscriptContainerDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_s);
  padding: var(--cc_size_spacing_l) var(--cc_size_spacing_xxl)
    var(--cc_size_spacing_xxl) var(--cc_size_spacing_xxl);
  background-color: var(--cc_color_background_3);
  max-height: 350px;
  overflow-y: scroll;
`;

const TranscriptHeaderDiv = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const AbsoluteDiv = styled.div`
  position: absolute;
  top: var(--cc_size_spacing_m);
  right: var(--cc_size_spacing_m);
  z-index: 100;
`;

const RelativeDiv = styled.div`
  position: relative;
`;

const LinearGradientDiv = styled.div`
  background: linear-gradient(transparent, var(--cc_color_background_3));
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 100;
  pointer-events: none;
`;

const CopyTranscriptButton = styled(Button)`
  margin-top: var(--cc_size_spacing_s);
`;
// #endregion

type PageTranscriptProps = {
  onClosePress: () => void;
  transcripts: CaptureOutputRequestObject['transcripts'];
  creatorName: string;
  createdAt: string;
};

const PageTranscript = ({
  onClosePress,
  transcripts,
  creatorName,
  createdAt,
}: PageTranscriptProps) => {
  const formattedCreatedAt = formatDate(parseISO(createdAt), 'PP');
  const startTime = new Date(transcripts[0].startTime);

  const fullTranscript = transcripts
    .map((transcript) => transcript.text)
    .join('\n');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(fullTranscript);
    toast.success(translate('projectPage.transcript.copyTranscript'));

    steven.elementClicked({
      id: 'ai_note_transcript_copy_button',
      content: 'projectPage.transcript.copyTranscript',
      type: 'button',
      action: 'ai_note_transcript_copy_button_clicked',
    });
  };

  return (
    <RelativeDiv>
      <AbsoluteDiv>
        <ButtonIcon
          iconName="close"
          color="secondary"
          onClick={onClosePress}
          accessibilityLabel={translate('common.close')}
        />
      </AbsoluteDiv>
      <TranscriptContainerDiv>
        <TranscriptHeaderDiv>
          <div>
            <Text as="p" size="m" weight={800}>
              {translate('projectPage.transcript.title')}
            </Text>
            <Text as="p" size="xs">
              {creatorName} • {formattedCreatedAt}
            </Text>
          </div>
          <CopyTranscriptButton
            color="secondary"
            icon={{ name: 'content-copy', position: 'before' }}
            onClick={handleCopy}
            design="outline"
            size="small"
          >
            {translate('projectPage.transcript.copyTranscript')}
          </CopyTranscriptButton>
        </TranscriptHeaderDiv>
        <div>
          {transcripts.map((transcript) => (
            <TranscriptItem
              key={transcript.id}
              text={transcript.text}
              seconds={
                (new Date(transcript.startTime).getTime() -
                  startTime.getTime()) /
                1000
              }
            />
          ))}
        </div>
      </TranscriptContainerDiv>
      <LinearGradientDiv />
    </RelativeDiv>
  );
};

export default PageTranscript;
